/**
 * Rotas do ata.
 *
 * Como no accounts, nao ha tela de login: a sessao entra por /sso e, sem
 * sessao, o usuario volta para o admin. A excecao e `/entrar` (caminho mantido
 * em portugues de proposito: outros apps o usam como destino de retorno do
 * SSO), que so existe em desenvolvimento local -- sem ela nao haveria como abrir a aplicacao numa
 * maquina que nao fala com o admin do cluster.
 *
 * Cada dominio novo entra como pasta em `resources/modules/<nome>/`, coletada
 * pelo glob abaixo, sem editar nada aqui.
 */
import { createRouter, createWebHistory } from 'vue-router';
import NotFoundPage from '@/pages/Error/ErrorPage.vue';
import SsoPage from '@/pages/Sso/SsoPage.vue';
import LocalLoginPage from '@/pages/Sso/LocalLoginPage.vue';
import { hasSession, logout, refreshToken, tokenNeedsRefresh } from '@/services/api/auth';
import { backToAdmin, DEFAULT_DESTINATION } from '@/services/sso.js';
import { PublicRoutes } from '@/enums/enums.js';
import { can, userCodes, registeredCodes } from '@/services/permission.js';

// Cada modulo em resources/modules/<nome>/routes.js exporta suas rotas por
// default. O glob e eager para que o array esteja pronto na criacao do router;
// as pages continuam carregando sob demanda pelo import() de cada rota.
const modules = import.meta.glob('@/modules/*/routes.js', { eager: true });
const moduleRoutes = Object.values(modules).flatMap((module) => module.default ?? []);

const routes = [
    ...moduleRoutes,
    {
        path: '/sso',
        name: 'sso',
        component: SsoPage,
        meta: { layout: 'blank' },
    },
    {
        // Entrada local de desenvolvimento. Publicada, a tela so explica que a
        // sessao vem do admin -- ela nao cria sessao nenhuma por conta propria:
        // quem emite o token e a API, e so quando ATA_LOCAL_LOGIN esta
        // ligado dos dois lados.
        path: '/entrar',
        name: 'local-login',
        component: LocalLoginPage,
        meta: { layout: 'blank', public: true },
    },
    {
        path: '/logout',
        name: 'logout',
        beforeEnter: async () => {
            try {
                await logout();
            } catch (error) {
                console.error(error);
            } finally {
                localStorage.clear();
            }

            // Sair daqui e sair da plataforma: o admin e quem tem a tela de
            // login para onde voltar. Sem pedido de volta, de proposito --
            // quem sai quer sair, e voltaria direto para onde acabou de deixar.
            if (window.LOCAL_LOGIN) {
                window.location.replace('/entrar');
                return false;
            }

            backToAdmin({ reason: 'logout' });

            return false;
        },
    },
    {
        path: '/',
        redirect: DEFAULT_DESTINATION,
    },
    {
        // Destino de quem foi barrado por permissao. Rota propria, e nao um
        // desvio para o login: mandar para o login quem ESTA logado e o laco
        // que ja trancou a aplicacao uma vez.
        path: '/no-access',
        name: 'no-access',
        component: () => import('@/pages/Error/NoAccessPage.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: NotFoundPage,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkExactActiveClass: 'active',
    scrollBehavior() {
        return { top: 0, behavior: 'smooth' };
    },
});

router.beforeEach(async (to) => {
    // Duas formas de ser publica, e as duas precisam existir: a lista casa por
    // caminho exato -- serve para /sso --, e `meta.public` serve para rota com
    // parametro, como /convite/:token, onde o caminho nunca e igual ao da
    // lista. Sem a segunda, o convite mandaria para o login quem ainda nem tem
    // conta.
    if (PublicRoutes.includes(to.path) || to.meta?.public) {
        return true;
    }

    // `to.fullPath` e o que a pessoa tentou abrir. Vai junto para o admin, que
    // depois do login traz ela de volta exatamente aqui -- e nao para a home.
    if (!hasSession()) {
        // Em desenvolvimento local nao ha admin para onde mandar: a tela de
        // entrada local e o unico caminho.
        if (window.LOCAL_LOGIN) {
            return { name: 'local-login', query: { from: to.fullPath } };
        }

        backToAdmin({ reason: 'sem-sessao', returnTo: to.fullPath });
        return false;
    }

    if (tokenNeedsRefresh()) {
        // O token local e emitido pela propria API do ata e nao existe
        // no liberty/auth: pedir /refresh para ele daria 401 e derrubaria a
        // sessao no meio da demonstracao. Vencido, a saida e entrar de novo.
        if (window.LOCAL_LOGIN) {
            localStorage.clear();
            return { name: 'local-login', query: { from: to.fullPath, reason: 'session-expired' } };
        }

        try {
            await refreshToken();
        } catch {
            localStorage.clear();
            backToAdmin({ reason: 'sessao-expirada', returnTo: to.fullPath });
            return false;
        }
    }

    // Permissao de tela. A rota declara o codigo em `meta.permission`; rota que
    // nao declara passa, para que tela nova nao nasca inacessivel.
    //
    // `can` libera em dois casos, os dois propositais: quando os codigos sao
    // desconhecidos, e quando a permissao exigida ainda nao foi cadastrada no
    // ambiente. Ver a regra em services/permission.js. A barreira de verdade e o
    // 403 da API; esta e conveniencia.
    if (!can(to.meta?.permission, userCodes(), registeredCodes())) {
        return { name: 'no-access', query: { from: to.fullPath } };
    }

    return true;
});

export default router;
